import {
  BottomTabBarProps,
  createBottomTabNavigator,
} from '@react-navigation/bottom-tabs';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Icon, ICONS } from 'components/Icon';
import { NavigationStacks, Screens, Stacks } from 'components/Navigation/types';
import { Txt } from 'components/Txt';
import { theme } from 'lib/theme';
import * as React from 'react';
import { ImageSourcePropType, Platform, View } from 'react-native';
import { SyncIndicator } from 'screens/SyncScreen/SyncIndicator';
import { Pressable } from 'components/Pressable';
import { BlurView } from 'expo-blur';
import { ProfileScreen } from 'screens/ProfileScreen/ProfileScreen';
import { SettingsScreen } from 'screens/SettingsScreen/SettingsScreen';
import { PageMain } from 'screens/ReportingTab/PageMain';

const TAB_BAR_HEIGHT = 62;

const Tab = createBottomTabNavigator();
type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.ROOT],
  Screens.RootPage
>;

const tabItems: {
  key: Screens.TAB_HOME | Screens.TAB_PROFILE | Screens.TAB_SETTINGS;
  name: string;
  component: React.FC;
  icon: ICONS;
  nativeIcon: ImageSourcePropType;
}[] = [
  {
    key: Screens.TAB_HOME,
    name: 'Home',
    component: PageMain,
    icon: ICONS.HouseSolid,
    nativeIcon: require('assets/menu/HouseSolid.png'),
  },
  {
    key: Screens.TAB_PROFILE,
    name: 'Profile',
    component: ProfileScreen,
    icon: ICONS.CircleUserSolid,
    nativeIcon: require('assets/menu/CircleUserSolid.png'),
  },
  {
    key: Screens.TAB_SETTINGS,
    name: 'Settings',
    component: SettingsScreen,
    icon: ICONS.GearComplexSolid,
    nativeIcon: require('assets/menu/GearComplexSolid.png'),
  },
];

const MyTabBar: React.FC<BottomTabBarProps> = ({
  state,
  navigation,
  insets,
}) => {
  return (
    <View
      style={{
        position: 'absolute',
        bottom: insets.bottom + 22,
        left: insets.left + 22,
        width: 280,
        zIndex: 999,
        height: TAB_BAR_HEIGHT,
        alignItems: 'stretch',
        flexDirection: 'row',
      }}
    >
      <View
        style={{
          backgroundColor:
            Platform.OS === 'android'
              ? theme.colors.white90
              : theme.colors.white50,
          flex: 1,
          borderRadius: 99,
          boxShadow: [
            {
              offsetX: 0,
              offsetY: 0,
              color: theme.colors.black10,
              blurRadius: 5,
            },
          ],
        }}
      >
        <BlurView
          intensity={20}
          tint="light"
          style={{
            borderRadius: 99,
            overflow: 'hidden',
            flexDirection: 'row',
            padding: 4,
            flex: 1,
          }}
        >
          {state.routes.map((route, index) => {
            const tabItem = tabItems.find((t) => t.key === route.name)!;
            const isFocused = state.index === index;
            const onPress = () => {
              const event = navigation.emit({
                type: 'tabPress',
                target: route.key,
                canPreventDefault: true,
              });
              if (!isFocused && !event.defaultPrevented) {
                navigation.navigate(route.name, route.params);
              }
            };
            return (
              <Pressable
                key={tabItem.name}
                onPress={onPress}
                pressedStyle={{
                  backgroundColor: theme.colors.black10,
                }}
                style={{
                  borderRadius: 99,
                  alignItems: 'center',
                  flex: 1,
                  justifyContent: 'center',
                  backgroundColor: isFocused ? theme.colors.black10 : undefined,
                  gap: 2,
                }}
              >
                <Icon
                  source={tabItem.icon}
                  size={22}
                  color={isFocused ? theme.colors.blueDark : undefined}
                />
                <Txt
                  numberOfLines={1}
                  size="xxsmall"
                  weight="medium"
                  style={
                    isFocused ? { color: theme.colors.blueDark } : undefined
                  }
                >
                  {tabItem.name}
                </Txt>
              </Pressable>
            );
          })}
        </BlurView>
      </View>
    </View>
  );
};

export const HomeWithoutSidebar: React.FC<Props> = () => {
  return (
    <View style={{ flex: 1 }}>
      <Tab.Navigator
        initialRouteName={tabItems[0]?.key}
        screenOptions={{
          headerShown: false,
          tabBarStyle: { position: 'absolute', left: -9999, top: -9999 },
          freezeOnBlur: true,
        }}
        tabBar={(props) => <MyTabBar {...props} />}
      >
        {tabItems.map((tabItem) => (
          <Tab.Screen
            key={tabItem.key}
            name={tabItem.key}
            component={tabItem.component}
          />
        ))}
      </Tab.Navigator>
      <SyncIndicator showSyncIcon />
    </View>
  );
};
